import { FormProvider, useForm } from "react-hook-form"
import { api_getHighSchoolLiveCardData, api_getHighSchoolPlayerRoster } from "../API/axios"
import { useEffect, useState } from "react"
import { resolvePlayerIDMap } from "../Utils/HeadToHeadUtils"
import { ZoneChart, ZoneChart2 } from "../Components/Charts/ZoneChart"
import { LocationPercentageChart } from "../Components/LocationAnalysisFrame"
import { PieChart } from "../Components/Charts/PieChart"
import { OursSportPitchPortionConfig } from "../Components/Charts/configs/person_configs"
import { resolveBatterLiveCardData, resolvePitcherLiveCardData } from "../Utils/OurSportsUtils"
import { wrapPieChartData } from "../Utils/PersonChartDataProcess"
import { ballColorMap } from "../Utils/ColorMap"
import D3DensityMap from "../Components/Charts/D3DensityMap"
import StatsInsight from '../style/images/logo.png'
import OurSport from '../style/images/tsl_logo.png'
import ELTA from '../style/images/ELTA.png'
import { EmptyFrame, LoadingFrame } from "../Components/PersonFrame"
import { FormSelectionBox } from "../Components/InputBox"
import { useNavigate } from 'react-router-dom'

const PitchNameMap = {
  FB: '直球',
  CB: '曲球',
  SL: '滑球',
  CH: '變速球',
  SC: '慢速曲球',
  SP: '指叉球',
  KN: '蝴蝶球',
  CT: '卡特球',
  SFF: '叉指變速球',
  FT: '二縫線',
  OT: '其他'
}


const OursSportsStream = () => {
  const form = useForm()
  const [roster, setRoster] = useState({ teams: [], options: {}, map: {} })
  const [playerData, setPlayerData] = useState({zoneData: {left: {}, right: {}, all: {}}, usageData: { usage: { all: [] } }, locationData: [], densityData: []})
  const [error, setError] = useState(true)
  const [loading, setLoading] = useState(false)
  const [type, setType] = useState('batter')
  const navigate = useNavigate()

  const handleOnSubmit = (result) => {
    setLoading(true)
    api_getHighSchoolLiveCardData(result).then((res) => {
      let data = {}
      if (res.data.length === 0) {
        setError(true)
        setLoading(false)
        return
      } 
      if (result.type === 'Batter') {
        data = resolveBatterLiveCardData(res.data)
      } else {
        data = resolvePitcherLiveCardData(res.data)
      }
      setError(false)
      setLoading(false)
      setType(result.type.toLowerCase())
      setPlayerData(data)
    })
  }
  
  const PlayerOptions = () => {
    const ttag = form.watch('Team')
    if (ttag === undefined || ttag === '') {
      return { options: [], optionTags: undefined }
    }
    return { options: roster.options[ttag], optionTags: roster.map[ttag] }
  }
  
  useEffect(() => {
    api_getHighSchoolPlayerRoster().then((res) => {
      const data = [...res.data]
      let processed = resolvePlayerIDMap(data)
      
      let teams = processed.teams.filter(team => team !== "隊伍");
      
      let final_roster = { teams: teams, options: {}, map: {} }
      for (const team of teams) {
        final_roster.options[team] = [ ...processed.options[team].pitcher, ...processed.options[team].batter ].filter((e) => !["全部投手","全部打者"].includes(e))
        final_roster.map[team] = { ...processed.map[team].pitcher, ...processed.map[team].batter }
      }
      setRoster(final_roster)
    })
  }, [])
  
  const handleOurSportClick = () => {
    navigate('/oursport');
  };

  const handleELTAClick = () => {
    navigate('/elta-raw-data');
  };

  return (
    <FormProvider {...form}>
      <div className="stream-card-wrapper">
        <div className="brand-header">
          <img src={StatsInsight} alt="" /> 
          <img 
            src={OurSport} 
            alt="OurSport" 
            onClick={handleOurSportClick} 
            style={{ cursor: 'pointer' }} 
          />
          <img 
            src={ELTA} 
            alt="ELTA" 
            onClick={handleELTAClick} 
            style={{ cursor: 'pointer' }} 
          />
        </div>
        <div className="stream-form">
          <form onSubmit={form.handleSubmit(handleOnSubmit)}>
            <div style={{ 
              display: 'flex', 
              gap: '10px', 
              width: '50%', 
              padding: '0 20px'
            }}>
              <div style={{ flex: '2' }}>
                <FormSelectionBox 
                  name='Team' 
                  options={roster.teams} 
                  optionTags={undefined} 
                  placeholder="隊伍" 
                />
              </div>
              <div style={{ flex: '1' }}>
                <FormSelectionBox 
                  name='PID' 
                  {...PlayerOptions()} 
                  placeholder="選手" 
                />
              </div>
              <div style={{ flex: '1' }}>
                <FormSelectionBox 
                  name='type' 
                  options={["Pitcher", "Batter"]} 
                  optionTags={{Pitcher: "投球", Batter: "打擊"}} 
                  placeholder="投球/打擊"
                />
              </div>
              <div style={{ flex: '0.5' }}>
                <button type="submit" style={{ width: '100%', height: '38px' }}>查詢</button>
              </div>
            </div>
          </form>
        </div>
        {
          loading ? <LoadingFrame /> :
            <>
              {
                error ? <EmptyFrame message="無相關資料，請重新設定搜尋條件" /> :
                  <div className="stream-cards">
                    <div className="stream-card-row">
                      <div className="stream-card-item">
                        {
                          type === 'batter' ? <ZoneChart2 title="出棒率" type='batter_swing_pct' zone={playerData.zoneData.all.swing} percentage nullValue={"0%"} /> :
                            <ZoneChart title="被打擊率" type="batter_avg" zone={playerData.zoneData.all.avg} />
                        }
                      </div>
                      <div className="stream-card-item">
                        {
                          type === 'batter' ? <ZoneChart title="打擊率" type="batter_avg" zone={playerData.zoneData.all.avg} /> :
                            <ZoneChart2 title="進壘位置比例" type="pitcher_pitch_pct" zone={playerData.zoneData.all.pitch} percentage nullValue={"0%"} />
                        }
                      </div>
                    </div>
                    <div className="stream-card-row">
                      <div className="stream-card-item">
                        <div className="stream-card-title">揮空熱力圖</div>
                        <D3DensityMap data={playerData.densityData} width={300} height={375} />
                      </div>
                      <div className="stream-card-item">
                        {
                          type === 'batter' ?
                            <LocationPercentageChart target_name='locationmap' data={{ title: '擊球分佈比率圖', values: playerData.locationData }} /> :
                            <>
                              <div className="stream-card-title">球種使用比例</div>
                              <div className="stream-portion-box">
                                <div className="stream-portion-chart">
                                  <PieChart data={wrapPieChartData(playerData.usageData.usage.all, ballColorMap)} config={OursSportPitchPortionConfig} />
                                </div>
                                <div className="stream-portion-display">
                                  {
                                    playerData.usageData.usage.all.map((e, index) => (
                                      <div className="stream-single-pitch" key={index}>
                                        <div className="ball-label-display" style={{backgroundColor: ballColorMap[e.tag]}} />
                                        <div className="ball-label-nametag">{PitchNameMap[e.tag]}</div>
                                        <div className="ball-label-portion">{parseInt(e.usage.slice(0, -1))}%</div>
                                      </div>
                                    ))
                                  }
                                </div>
                              </div>
                            </>
                        }
                      </div>
                    </div>
                  </div>
              }
            </>
        }
      </div>
    </FormProvider>
  )
}

export default OursSportsStream